import { Activity, Brain, Shield, Zap, Heart, LineChart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Hero } from '../../components/Hero';

export function Technology() {
  return (
    <div>
      <Hero
        title="Our Technology"
        description="Advanced diagnostic and treatment technologies enabling precise neurological care and improved patient outcomes."
        image="https://images.unsplash.com/photo-1581093458791-9f3c3900df4b?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-16">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">VitalScan ANS Testing System</h2>
            <p className="text-xl text-gray-600">
              State-of-the-art autonomic nervous system assessment technology providing comprehensive 
              evaluation of cardiovascular autonomic function.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Diagnostic Capabilities</h3>
              <p className="text-gray-600 mb-6">
                The VitalScan ANS Testing System represents the cutting edge in autonomic nervous system 
                assessment technology. This sophisticated platform enables our team to perform detailed 
                evaluations of autonomic function through a series of non-invasive tests and measurements.
              </p>
              <p className="text-gray-600">
                Using advanced biosensors and real-time monitoring capabilities, VitalScan provides 
                comprehensive data about how your autonomic nervous system responds to various physiological 
                challenges, helping us diagnose and track conditions affecting autonomic function.
              </p>
            </div>
            <div className="bg-blue-50 rounded-lg p-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Features</h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Heart className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h5 className="font-semibold text-gray-900">Heart Rate Variability Analysis</h5>
                    <p className="text-gray-600">Advanced HRV assessment for autonomic balance evaluation</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Activity className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h5 className="font-semibold text-gray-900">Sudomotor Function Testing</h5>
                    <p className="text-gray-600">Evaluation of small nerve fiber function and sweat gland response</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Brain className="h-6 w-6 text-blue-600 mr-3 mt-1" />
                  <div>
                    <h5 className="font-semibold text-gray-900">Valsalva Response Analysis</h5>
                    <p className="text-gray-600">Assessment of cardiovascular autonomic reflexes</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Zap className="h-8 w-8 text-blue-600 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Rapid Assessment</h4>
              <p className="text-gray-600">
                Complete autonomic function evaluation in under 30 minutes, providing immediate 
                insights into autonomic health.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <Shield className="h-8 w-8 text-blue-600 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Non-invasive Testing</h4>
              <p className="text-gray-600">
                Comfortable, painless procedures using advanced biosensors for comprehensive 
                autonomic assessment.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md">
              <LineChart className="h-8 w-8 text-blue-600 mb-4" />
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Detailed Analytics</h4>
              <p className="text-gray-600">
                Advanced data analysis providing detailed insights into autonomic function 
                and treatment effectiveness.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Clinical Applications</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Diagnostic Capabilities</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Early detection of autonomic dysfunction</li>
                  <li>• Assessment of cardiovascular autonomic neuropathy</li>
                  <li>• Evaluation of orthostatic intolerance</li>
                  <li>• Monitoring of POTS and related conditions</li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Treatment Monitoring</h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Tracking treatment effectiveness</li>
                  <li>• Progression monitoring of autonomic conditions</li>
                  <li>• Adjustment of therapeutic interventions</li>
                  <li>• Long-term outcome assessment</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Experience Advanced Care</h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Our investment in advanced technology like the VitalScan ANS Testing System reflects our 
            commitment to providing the highest standard of neurological care. Contact us to learn 
            how our advanced diagnostic capabilities can benefit your health.
          </p>
          <Link
            to="/contact"
            className="bg-blue-600 text-white px-8 py-3 rounded-md font-semibold hover:bg-blue-700 transition-colors"
          >
            Schedule an Assessment
          </Link>
        </div>
      </div>
    </div>
  );
}